import React, { useCallback } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { accountsAPI } from 'api/accounting/accountsAPI';
import { IAccountModel } from 'typings/onboarding/account';
import { generateAccountFullName } from 'helpers/accountsHelpers';
import { AutocompleteOption, AutocompleteProps } from '@ui';

// components
import { FormAutocomplete } from '@ui';

export interface RecipientBankAccountAutocompleteProps
  extends AutocompleteProps<IAccountModel> {
  senderAccountNumber?: string;
  addOtherBankAccountOption?: boolean;
}

export const OTHER_BANK_ACCOUNT_OPTION = 'other-bank-account';

const RecipientBankAccountAutocomplete = ({
  senderAccountNumber,
  addOtherBankAccountOption,
  ...rest
}: RecipientBankAccountAutocompleteProps) => {
  const { t } = useTranslation(['transfers', 'custom_errors', 'form']);

  const fetchOptionsCallback = useCallback(
    async (search: string, page: number, limit: number) => {
      if (!senderAccountNumber) {
        return {
          total: 0,
          options: [],
        };
      }

      const { data, total } =
        await accountsAPI.fetchEligibleAccountsForInternalTransfer(
          senderAccountNumber,
          {
            search,
            page,
            limit,
          },
        );

      let totalRecords = total;

      let result: AutocompleteOption<IAccountModel>[] = data.map((e) => {
        const {
          accountNumber,
          accountName,
          accountNickname,
          currencyCode,
          balance,
        } = e;

        const name = generateAccountFullName(
          accountName,
          accountNumber,
          accountNickname,
          currencyCode,
          balance,
        );

        return {
          id: e.accountNumber,
          model: e,
          label: <OptionContentWrapper>{name}</OptionContentWrapper>,
        };
      });

      if (addOtherBankAccountOption && page === 1) {
        result = [
          {
            id: OTHER_BANK_ACCOUNT_OPTION,
            label: t('internal_transfer.other_bank_account_option'),
          },
          ...result,
        ];

        totalRecords++;
      }

      return { total: totalRecords, options: result };
    },
    [senderAccountNumber, addOtherBankAccountOption],
  );

  return <FormAutocomplete {...rest} fetchData={fetchOptionsCallback} />;
};

const OptionContentWrapper = styled.div`
  display: flex;
  overflow: hidden;
  max-width: 100%;
`;

export default RecipientBankAccountAutocomplete;
